import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import CreateClassModal from "../../modals/CreateClassModal";
import SyncGoogleClassroomModal from "../../modals/SyncGoogleClassroomModal";
import DeleteStudentModal from "../../modals/DeleteStudentModal";
import { createClass } from "../../../utils/createClass";
import { editClass } from "../../../utils/editClass";
import { syncGoogleClassroomClasses } from "../../../utils/syncGoogleClassroomClasses";
import { toast } from "react-toastify";
import ClassCard from "./ClassCard";
import ClassCodeRow from "./ClassCodeRow";
import ImportStudentsModal from "../../modals/ImportStudentsModal";
import ManageClass from "./ManageClass";
import useUserStore from "../../../stores/user";
import { useClasses } from "./hooks/useClasses";
import { ClassPagination } from "./ClassPagination";
import { useT } from "../../../stores/uiLang";
import { TD_T } from "../../../i18n/teacherDashboard";

export default function ClassManagement() {
  const t = useT(TD_T);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isSyncGoogleModalOpen, setIsSyncGoogleModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isDeleteStudentModalOpen, setIsDeleteStudentModalOpen] =
    useState(false);
  const [selectedClass, setSelectedClass] = useState<any | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(6); // Show 6 classes per page (2 rows of 3 in grid)

  const [importResults, setImportResults] = useState<{
    message?: string;
    importedEmails: string[];
    newImportedStudents: {
      firstName: string;
      lastName: string;
      email: string;
      temporaryPassword: string;
    }[];
    failedEmails: { email: string; reason: string }[];
    error?: string;
  } | null>(null);
  const [syncResults, setSyncResults] = useState<{
    message?: string;
    importedEmails: string[];
    newImportedStudents: {
      firstName: string;
      lastName: string;
      email: string;
      temporaryPassword: string;
    }[];
    failedEmails: { email: string; reason: string }[];
    error?: string;
  } | null>(null);
  const [rosterResults, setRosterResults] = useState<{
    message?: string;
    importedEmails: string[];
    newImportedStudents: {
      firstName: string;
      lastName: string;
      email: string;
      temporaryPassword: string;
    }[];
    failedEmails: { email: string; reason: string }[];
    error?: string;
  } | null>(null);

  const { user } = useUserStore();
  const { classes, loading, totalClasses, fetchTeacherClasses } = useClasses({
    user,
    searchTerm,
    currentPage,
    limit,
  });

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    // If ?manage=classId is present, set selectedClass
    const manageId = searchParams.get("manage");
    if (manageId && classes.length > 0) {
      const cls = classes.find((c) => c.id === manageId);
      if (cls) setSelectedClass(cls);
      else setSelectedClass(null);
    } else if (!manageId) {
      setSelectedClass(null);
    }
  }, [searchParams, classes]);

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleCreateClass = async (data: {
    name: string;
    description?: string;
    semester: string;
    createJoinCode: boolean;
    settings: {
      class_time: string;
    };
    grade_type: string;
  }): Promise<{ error: string | null }> => {
    const { error } = await createClass(data);
    if (error) {
      toast.error(error);
    } else {
      toast.success("Class created successfully!");
      setIsCreateModalOpen(false);
      await fetchTeacherClasses();
    }

    return { error: error ?? null };
  };

  const handleUpdateClass = async (data: {
    name: string;
    description?: string;
    semester: string;
    settings: {
      class_time: string;
    };
    grade_type: string;
    createJoinCode?: boolean;
  }): Promise<{ error: string | null }> => {
    if (!selectedClass) {
      return { error: "No class selected" };
    }

    const { message, error, join_code } = await editClass({
      id: selectedClass.id,
      ...data,
      createJoinCode: data.createJoinCode ?? false,
    });

    if (error) {
      toast.error(error);
      return { error };
    }

    toast.success(message);
    await fetchTeacherClasses();
    return { error: null };
  };

  function automaticallyDownloadStudentsInfo(importResults: any) {
    const csvContent = [
      ["First Name", "Last Name", "Email", "Temporary Password"],
      ...importResults.newImportedStudents.map((student) => [
        student.firstName,
        student.lastName,
        student.email,
        student.temporaryPassword,
      ]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "new_students.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  const handleSyncGoogleClasses = async (selectedClasses: any[]) => {
    try {
      const result = await syncGoogleClassroomClasses(selectedClasses);

      if (result.error) {
        toast.error(result.error);
        return;
      }

      if (result) {
        setSyncResults({
          message: result.message,
          importedEmails: result.importedEmails || [],
          newImportedStudents: result.newImportedStudents || [],
          failedEmails: result.failedEmails || [],
        });

        if (result.newImportedStudents.length > 0) {
          automaticallyDownloadStudentsInfo(result);
        }

        if (result.importedCount > 0) {
          toast.success(
            result.message ||
              `Successfully synced ${result.createdClasses} classes with ${result.importedCount} students imported`
          );
        }

        if (result.failedCount > 0) {
          toast.warning(
            `${result.failedCount} students failed to import. Check the details below.`
          );
        }

        if (result.errors && result.errors.length > 0) {
          toast.warning(
            `${result.errors.length} classes had issues. Check the console for details.`
          );
          console.error("Sync errors:", result.errors);
        }
      }

      await fetchTeacherClasses(); // Refresh the classes list
    } catch (error) {
      toast.error("Failed to sync classes from Google Classroom");
      console.error(error);
    }
  };

  const handleRosterComplete = async (results: any) => {
    // Handle class deletion
    if (results.type === "class_deleted") {
      await fetchTeacherClasses(); // Refresh the classes list
      return;
    }

    // Handle roster results
    setRosterResults({
      message: results.message,
      importedEmails: results.importedEmails || [],
      newImportedStudents: results.newImportedStudents || [],
      failedEmails: results.failedEmails || [],
    });

    await fetchTeacherClasses(); // Refresh the classes list
  };

  if (selectedClass) {
    return (
      <ManageClass
        cls={selectedClass}
        onBack={() => {
          setSearchParams({});
          setSelectedClass(null);
        }}
        onUpdate={handleUpdateClass}
      />
    );
  }

  return (
    <section className="mb-10">
      {/* Header Actions */}
      {/* Run E2 J2 round 3: header and buttons wrap on phones (390 px). */}
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-5">
        <h2 className="text-lg font-medium primary-text">{t.classManagement}</h2>
        <div className="flex flex-wrap gap-2 sm:gap-3">
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="btn btn-small"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="-ml-1 mr-2 h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                clipRule="evenodd"
              />
            </svg>
            {t.addClass}
          </button>
          <button
            onClick={() => {
              setSyncResults(null); // Clear previous sync results
              setIsSyncGoogleModalOpen(true);
            }}
            className="btn btn-small btn-secondary"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="-ml-1 mr-2 h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            {t.syncGoogle}
          </button>
          <button
            className="btn btn-small btn-secondary"
            onClick={() => setIsImportModalOpen(true)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="-ml-1 mr-2 h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
              />
            </svg>
            {t.importStudents}
          </button>
          <button
            className="btn btn-small btn-danger"
            onClick={() => setIsDeleteStudentModalOpen(true)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="-ml-1 mr-2 h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
            {t.deleteStudent}
          </button>
        </div>
      </div>

      {/* Search Input */}
      <div className="mb-6">
        <div className="max-w-md">
          <label htmlFor="search" className="sr-only">
            {t.searchClasses}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-5 w-5 text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <input
              id="search"
              name="search"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder={t.searchClassesPh}
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Import Results */}
      {importResults && (
        <div className="bg-white shadow rounded-lg border border-gray-200 overflow-hidden mb-8">
          <div className="px-6 py-5 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                {t.importResults}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {t.importSummary}
              </p>
            </div>
            <button
              onClick={() => setImportResults(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div className="p-6">
            {importResults.message && (
              <p className="text-sm text-green-700 mb-4">
                ✅ {importResults.message}
              </p>
            )}
            {importResults.importedEmails.length > 0 && (
              <div className="mb-2">
                <p className="text-sm text-green-700">
                  ✅ {importResults.importedEmails.length} Students Imported
                </p>
                <ul className="list-disc ml-6 text-xs text-green-800">
                  {importResults.importedEmails.map((email, i) => (
                    <li key={`${i}-${email}`}>{email}</li>
                  ))}
                </ul>
              </div>
            )}
            {importResults.newImportedStudents.length > 0 && (
              <div className="mb-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-blue-700">
                    🆕 {importResults.newImportedStudents.length} New Students
                    Created
                  </p>
                  <button
                    onClick={() => {
                      const csvContent = [
                        [
                          "First Name",
                          "Last Name",
                          "Email",
                          "Temporary Password",
                        ],
                        ...importResults.newImportedStudents.map((student) => [
                          student.firstName,
                          student.lastName,
                          student.email,
                          student.temporaryPassword,
                        ]),
                      ]
                        .map((row) => row.join(","))
                        .join("\n");

                      const blob = new Blob([csvContent], {
                        type: "text/csv;charset=utf-8;",
                      });
                      const link = document.createElement("a");
                      const url = URL.createObjectURL(blob);
                      link.setAttribute("href", url);
                      link.setAttribute("download", "new_students.csv");
                      link.style.visibility = "hidden";
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="-ml-1 mr-1.5 h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                      />
                    </svg>
                    {t.downloadNew}
                  </button>
                </div>
                <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-yellow-400"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700">
                        Please download this information now. For security
                        reasons, the temporary passwords will not be accessible
                        later.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {importResults.failedEmails.length > 0 && (
              <div>
                <p className="text-sm text-yellow-700">⚠️ Failed:</p>
                <ul className="list-disc ml-6 text-xs text-yellow-800">
                  {importResults.failedEmails.map(({ email, reason }, i) => (
                    <li key={`${i}-${email}`}>
                      {email} — {reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sync Results */}
      {syncResults && (
        <div className="bg-white shadow rounded-lg border border-gray-200 overflow-hidden mb-8">
          <div className="px-6 py-5 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                {t.syncResults}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {t.syncSummary}
              </p>
            </div>
            <button
              onClick={() => setSyncResults(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div className="p-6">
            {syncResults.message && (
              <p className="text-sm text-green-700 mb-4">
                ✅ {syncResults.message}
              </p>
            )}
            {syncResults.importedEmails.length > 0 && (
              <div className="mb-2">
                <p className="text-sm text-green-700">
                  ✅ {syncResults.importedEmails.length} Students Imported
                </p>
                <ul className="list-disc ml-6 text-xs text-green-800">
                  {syncResults.importedEmails.map((email, i) => (
                    <li key={`${i}-${email}`}>{email}</li>
                  ))}
                </ul>
              </div>
            )}
            {syncResults.newImportedStudents.length > 0 && (
              <div className="mb-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-blue-700">
                    🆕 {syncResults.newImportedStudents.length} New Students
                    Created
                  </p>
                  <button
                    onClick={() => {
                      const csvContent = [
                        [
                          "First Name",
                          "Last Name",
                          "Email",
                          "Temporary Password",
                        ],
                        ...syncResults.newImportedStudents.map((student) => [
                          student.firstName,
                          student.lastName,
                          student.email,
                          student.temporaryPassword,
                        ]),
                      ]
                        .map((row) => row.join(","))
                        .join("\n");

                      const blob = new Blob([csvContent], {
                        type: "text/csv;charset=utf-8;",
                      });
                      const link = document.createElement("a");
                      const url = URL.createObjectURL(blob);
                      link.setAttribute("href", url);
                      link.setAttribute(
                        "download",
                        "google_classroom_new_students.csv"
                      );
                      link.style.visibility = "hidden";
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="-ml-1 mr-1.5 h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                      />
                    </svg>
                    {t.downloadNew}
                  </button>
                </div>
                <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-yellow-400"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700">
                        Please download this information now. For security
                        reasons, the temporary passwords will not be accessible
                        later.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {syncResults.failedEmails.length > 0 && (
              <div>
                <p className="text-sm text-yellow-700">⚠️ Failed:</p>
                <ul className="list-disc ml-6 text-xs text-yellow-800">
                  {syncResults.failedEmails.map(({ email, reason }, i) => (
                    <li key={`${i}-${email}`}>
                      {email} — {reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Roster Results */}
      {rosterResults && (
        <div className="bg-white shadow rounded-lg border border-gray-200 overflow-hidden mb-8">
          <div className="px-6 py-5 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                {t.rosterResults}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {t.rosterSummary}
              </p>
            </div>
            <button
              onClick={() => setRosterResults(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div className="p-6">
            {rosterResults.message && (
              <p className="text-sm text-green-700 mb-4">
                ✅ {rosterResults.message}
              </p>
            )}
            {rosterResults.importedEmails.length > 0 && (
              <div className="mb-2">
                <p className="text-sm text-green-700">
                  ✅ {rosterResults.importedEmails.length} Students Imported
                </p>
                <ul className="list-disc ml-6 text-xs text-green-800">
                  {rosterResults.importedEmails.map((email, i) => (
                    <li key={`${i}-${email}`}>{email}</li>
                  ))}
                </ul>
              </div>
            )}
            {rosterResults.newImportedStudents.length > 0 && (
              <div className="mb-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-blue-700">
                    🆕 {rosterResults.newImportedStudents.length} New Students
                    Created
                  </p>
                  <button
                    onClick={() => {
                      const csvContent = [
                        [
                          "First Name",
                          "Last Name",
                          "Email",
                          "Temporary Password",
                        ],
                        ...rosterResults.newImportedStudents.map((student) => [
                          student.firstName,
                          student.lastName,
                          student.email,
                          student.temporaryPassword,
                        ]),
                      ]
                        .map((row) => row.join(","))
                        .join("\n");

                      const blob = new Blob([csvContent], {
                        type: "text/csv;charset=utf-8;",
                      });
                      const link = document.createElement("a");
                      const url = URL.createObjectURL(blob);
                      link.setAttribute("href", url);
                      link.setAttribute("download", "roster_new_students.csv");
                      link.style.visibility = "hidden";
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="-ml-1 mr-1.5 h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                      />
                    </svg>
                    {t.downloadNew}
                  </button>
                </div>
                <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-yellow-400"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700">
                        Please download this information now. For security
                        reasons, the temporary passwords will not be accessible
                        later.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {rosterResults.failedEmails.length > 0 && (
              <div>
                <p className="text-sm text-yellow-700">⚠️ Failed:</p>
                <ul className="list-disc ml-6 text-xs text-yellow-800">
                  {rosterResults.failedEmails.map(({ email, reason }, i) => (
                    <li key={`${i}-${email}`}>
                      {email} — {reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      <>
        <div className="relative min-h-[600px]">
          {!loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
              {classes.length === 0 ? (
                <p className="text-sm text-gray-500">
                  {searchTerm
                    ? "No classes found matching your search."
                    : "No classes available."}
                </p>
              ) : (
                classes.map((cls) => (
                  <ClassCard
                    key={cls.id}
                    cls={cls}
                    onManage={() => setSearchParams({ manage: cls.id })}
                    onRosterComplete={handleRosterComplete}
                    onDelete={fetchTeacherClasses}
                  />
                ))
              )}
            </div>
          )}

          {loading && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-lg">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-2"></div>
                <p className="text-sm text-gray-500">
                  {searchTerm ? "Searching classes..." : "Loading classes..."}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalClasses > limit && (
          <ClassPagination
            currentPage={currentPage}
            totalPages={Math.ceil(totalClasses / limit)}
            totalClasses={totalClasses}
            limit={limit}
            onPageChange={setCurrentPage}
          />
        )}
      </>

      {/* Join Codes Table */}
      <div className="bg-white shadow rounded-lg border border-gray-200 overflow-hidden mb-8">
        <div className="px-6 py-5 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-medium text-gray-900">
            {t.joinCodes}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {t.joinCodesHelp}
          </p>
        </div>
        <div className="p-6 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead>
              <tr>
                <th className="px-6 py-3 text-left text-gray-500 uppercase tracking-wider">
                  {t.className}
                </th>
                <th className="px-6 py-3 text-left text-gray-500 uppercase tracking-wider">
                  {t.joinCode}
                </th>
                <th className="px-6 py-3 text-left text-gray-500 uppercase tracking-wider">
                  {t.expiration}
                </th>
                <th className="px-6 py-3 text-left text-gray-500 uppercase tracking-wider">
                  {t.actions}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {classes
                .filter((cls) => cls.join_code)
                .map((cls) => (
                  <ClassCodeRow
                    key={cls.id}
                    id={cls.id}
                    name={cls.name}
                    joinCode={cls.join_code}
                    expiration="Never"
                    onJoinCodeUpdate={(newJoinCode) => {
                      // Refresh classes to get updated join code
                      fetchTeacherClasses();
                    }}
                  />
                ))}
            </tbody>
          </table>
        </div>
      </div>
      <CreateClassModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreate={handleCreateClass}
      />
      <ImportStudentsModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onComplete={async (results) => {
          setImportResults(results);
          if (results.newImportedStudents.length > 0) {
            automaticallyDownloadStudentsInfo(results);
          }
          if (!results.error) {
            await fetchTeacherClasses(); // Refresh classes data after successful import
          }
        }}
      />
      <SyncGoogleClassroomModal
        isOpen={isSyncGoogleModalOpen}
        onClose={() => {
          setIsSyncGoogleModalOpen(false);
        }}
        onSync={handleSyncGoogleClasses}
      />
      <DeleteStudentModal
        isOpen={isDeleteStudentModalOpen}
        onClose={() => setIsDeleteStudentModalOpen(false)}
        onStudentDeleted={() => {
          // Refresh classes to update student counts
          fetchTeacherClasses();
        }}
      />
    </section>
  );
}
